<template>
  <v-dialog
    :model-value="dialog"
    max-width="380"
    @update:model-value="(value) => $emit('update:dialog', value)"
  >
    <v-card class="rounded-2 pa-2" data-cy="thermostat-config-dialog">
      <!--Header-->
      <v-card-title class="d-flex align-start pb-2 config-header">
        <div>
          <div class="text-primary text-subtitle-1 font-weight-bold">
            <span class="status-dot mr-1" :class="statusDotClass" />
            {{ thermostatInfo.controlInfo.friendlyName }}
          </div>
          <div class="text-sonicSilver text-caption mt-n1">
            {{ siteName }}
          </div>
        </div>
        <v-spacer />
        <!--Dirty Icon-->
        <v-tooltip location="bottom" content-class="control-tooltip">
          <template v-slot:activator="{ props }">
            <v-icon
              v-if="isThermostatDirty || isDirty.isOutOfRange"
              class="control-dirty-icon mt-1 mr-2"
              :color="isDirty.isOutOfRange ? 'warning' : 'accentOrange'"
              v-bind="props"
              data-cy="config-dirty-icon"
            >
              {{
                isDirty.isOutOfRange ? 'mdi-alert-outline' : 'mdi-alert-circle'
              }}
            </v-icon>
          </template>
          <!--Warnings-->
          <ThermostatAlert
            :isDirty="isDirty"
            :isThermostatDirty="isThermostatDirty"
            :getThermostatNewSettings="getThermostatNewSettings"
            :getTemperaturesOutOfRange="getTemperaturesOutOfRange"
          />
        </v-tooltip>
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          data-cy="config-close"
          @click="$emit('update:dialog', false)"
        />
      </v-card-title>
      <v-divider />
      <v-card-text class="pb-0">
        <!--Hero: temperature + setpoint-->
        <v-row density="comfortable" class="mt-1">
          <v-col cols="6" class="d-flex flex-column">
            <span class="text-sonicSilver text-caption"
              >Temperature (F&deg;)</span
            >
            <v-tooltip
              v-if="temperatureConversionTooltipEnabled && thermostatInfo.temperature != null"
              location="top"
            >
              <template v-slot:activator="{ props }">
                <span
                  class="text-h3 font-weight-bold"
                  :class="`text-${activityColor}`"
                  data-cy="config-temperature"
                  v-bind="props"
                >
                  {{ thermostatInfo.temperature }}&deg;
                </span>
              </template>
              <span>{{ fahrenheitToCelsius(thermostatInfo.temperature) }}°C</span>
            </v-tooltip>
            <span
              v-else-if="thermostatInfo.temperature != null"
              class="text-h3 font-weight-bold"
              :class="`text-${activityColor}`"
              data-cy="config-temperature"
            >
              {{ thermostatInfo.temperature }}&deg;
            </span>
            <span
              v-else
              class="text-h3 font-weight-bold"
              :class="`text-${activityColor}`"
              data-cy="config-temperature"
            >
              —
            </span>
            <!--Action line-->
            <span class="text-caption font-weight-bold" data-cy="config-action">
              <template v-if="activity === 'heating' || activity === 'cooling'">
                <span :class="`text-${activityColor}`">
                  {{ activity === 'heating' ? 'Heating' : 'Cooling' }}
                </span>
                <v-tooltip v-if="temperatureConversionTooltipEnabled" location="top">
                  <template v-slot:activator="{ props }">
                    <span
                      class="text-sonicSilver font-weight-regular"
                      v-bind="props"
                    >
                      to {{ actionTarget }}&deg;
                    </span>
                  </template>
                  <span>{{ fahrenheitToCelsius(actionTarget) }}°C</span>
                </v-tooltip>
                <span
                  v-else
                  class="text-sonicSilver font-weight-regular"
                >
                  to {{ actionTarget }}&deg;
                </span>
              </template>
              <span v-else-if="activity === 'off'" class="text-sonicSilver">
                System off
              </span>
              <span v-else class="text-sonicSilver">Space satisfied</span>
            </span>
          </v-col>
          <!--Setpoint panel-->
          <v-col cols="6">
            <v-card
              variant="outlined"
              class="setpoint-panel pa-3"
              :class="{
                'setpoint-panel--off': isMilesight && staged.mode === 'Off',
              }"
            >
              <template v-if="isMilesight">
                <div class="text-sonicSilver text-caption text-center">
                  Setpoint
                </div>
                <div
                  class="text-h5 text-primary font-weight-bold text-center"
                  data-cy="target-value"
                >
                  <v-tooltip
                    v-if="temperatureConversionTooltipEnabled && staged.mode !== 'Off'"
                    location="top"
                  >
                    <template v-slot:activator="{ props }">
                      <span v-bind="props">{{ staged.targetTemperature }}º</span>
                    </template>
                    <span>{{ fahrenheitToCelsius(staged.targetTemperature) }}°C</span>
                  </v-tooltip>
                  <span v-else-if="staged.mode !== 'Off'">{{ staged.targetTemperature }}º</span>
                  <span v-else>—</span>
                </div>
                <div class="d-flex flex-column align-center mt-2">
                  <v-btn
                    icon="mdi-chevron-up"
                    variant="outlined"
                    color="primary"
                    size="small"
                    class="step-btn mb-2"
                    :disabled="!canEdit"
                    data-cy="target-step-up"
                    @click="stepSetpoint('targetTemperature', 1)"
                  />
                  <v-btn
                    icon="mdi-chevron-down"
                    variant="outlined"
                    color="primary"
                    size="small"
                    class="step-btn"
                    :disabled="!canEdit"
                    data-cy="target-step-down"
                    @click="stepSetpoint('targetTemperature', -1)"
                  />
                </div>
                <v-tooltip v-if="temperatureConversionTooltipEnabled" location="top">
                  <template v-slot:activator="{ props }">
                    <div
                      class="text-sonicSilver text-caption text-center mt-2"
                      v-bind="props"
                    >
                      {{ targetSettings.min }}–{{ targetSettings.max }}&deg;
                    </div>
                  </template>
                  <span>
                    {{ fahrenheitToCelsius(targetSettings.min) }}–{{
                      fahrenheitToCelsius(targetSettings.max)
                    }}°C
                  </span>
                </v-tooltip>
                <div
                  v-else
                  class="text-sonicSilver text-caption text-center mt-2"
                >
                  {{ targetSettings.min }}–{{ targetSettings.max }}&deg;
                </div>
              </template>
              <template v-else>
                <div class="d-flex justify-space-around">
                  <div
                    v-for="column in pelicanSetpointColumns"
                    :key="column.key"
                    class="d-flex flex-column align-center"
                  >
                    <v-icon size="x-small" :color="column.color">
                      {{ column.icon }}
                    </v-icon>
                    <v-tooltip v-if="temperatureConversionTooltipEnabled" location="top">
                      <template v-slot:activator="{ props }">
                        <span
                          class="text-h5 font-weight-bold"
                          :class="`text-${column.color}`"
                          v-bind="props"
                        >
                          {{ staged[column.key] }}&deg;
                        </span>
                      </template>
                      <span>{{ fahrenheitToCelsius(staged[column.key]) }}°C</span>
                    </v-tooltip>
                    <span
                      v-else
                      class="text-h5 font-weight-bold"
                      :class="`text-${column.color}`"
                    >
                      {{ staged[column.key] }}&deg;
                    </span>
                    <v-btn
                      icon="mdi-chevron-up"
                      variant="outlined"
                      color="primary"
                      size="small"
                      class="step-btn mt-2 mb-2"
                      :disabled="!canEdit"
                      :data-cy="`${column.key}-step-up`"
                      @click="stepSetpoint(column.key, 1)"
                    />
                    <v-btn
                      icon="mdi-chevron-down"
                      variant="outlined"
                      color="primary"
                      size="small"
                      class="step-btn"
                      :disabled="!canEdit"
                      :data-cy="`${column.key}-step-down`"
                      @click="stepSetpoint(column.key, -1)"
                    />
                    <v-tooltip v-if="temperatureConversionTooltipEnabled" location="top">
                      <template v-slot:activator="{ props }">
                        <span
                          class="text-sonicSilver text-caption mt-2"
                          v-bind="props"
                        >
                          {{ column.min }}–{{ column.max }}&deg;
                        </span>
                      </template>
                      <span>
                        {{ fahrenheitToCelsius(column.min) }}–{{
                          fahrenheitToCelsius(column.max)
                        }}°C
                      </span>
                    </v-tooltip>
                    <span
                      v-else
                      class="text-sonicSilver text-caption mt-2"
                    >
                      {{ column.min }}–{{ column.max }}&deg;
                    </span>
                  </div>
                </div>
              </template>
            </v-card>
          </v-col>
        </v-row>
        <!--CO2-->
        <v-row
          density="comfortable"
          v-if="thermostatInfo.co2Level"
          class="mt-3"
        >
          <v-col class="control-co2-widget" @click="handleCO2Action">
            <CO2Widget :controlData="thermostatInfo" />
          </v-col>
        </v-row>
        <!--Mode / Fan-->
        <v-row density="comfortable" class="mt-3">
          <v-col cols="6">
            <v-select
              density="compact"
              :disabled="!canEdit"
              v-model="staged.mode"
              :items="systemModes"
              label="System"
              variant="outlined"
              class="text-primary"
              :hide-details="true"
              data-cy="config-mode"
            />
          </v-col>
          <v-col cols="6">
            <v-select
              density="compact"
              :disabled="!canEdit"
              v-model="staged.fanStatus"
              :items="fanModes"
              label="Fan"
              variant="outlined"
              class="text-primary"
              :hide-details="true"
              data-cy="config-fan"
            />
          </v-col>
        </v-row>
        <!--Front Keypad-->
        <v-row density="comfortable" class="mt-3">
          <v-col>
            <v-card
              variant="outlined"
              class="d-flex align-center justify-space-between px-3 py-1"
            >
              <span class="text-primary text-caption font-weight-bold">
                <v-icon color="primary" size="small"> mdi-dialpad </v-icon>
                Front Keypad
              </span>
              <v-switch
                hide-details
                :disabled="!canEdit"
                color="primary"
                inset
                density="compact"
                v-model="staged.keypadLocked"
                :label="staged.keypadLocked ? 'Locked' : 'Unlocked'"
                data-cy="config-keypad"
              />
            </v-card>
          </v-col>
        </v-row>
        <v-alert
          v-if="applyErrorMessage"
          type="error"
          :icon="false"
          density="compact"
          variant="tonal"
          class="mt-2 text-caption"
          data-cy="config-error"
        >
          {{ applyErrorMessage }}
        </v-alert>
      </v-card-text>
      <!--Apply bar-->
      <v-card-text class="pt-2 pb-1">
        <div class="apply-slot">
          <v-row v-if="isThermostatDirty" density="comfortable">
            <v-col v-if="!isThermostatSetLoading" cols="4">
              <v-btn
                class="rounded-2"
                color="primary"
                variant="outlined"
                block
                data-cy="config-discard"
                @click="resetAllValues"
              >
                <div class="text-subtitle-2 text-truncate font-weight-bold">
                  Discard
                </div>
              </v-btn>
            </v-col>
            <v-col>
              <v-btn
                :disabled="isThermostatSetLoading || !userCanUpdate"
                :loading="isThermostatSetLoading"
                class="rounded-2"
                color="accentOrange"
                variant="flat"
                block
                data-cy="config-apply"
                @click="handleApply"
              >
                <div
                  class="text-subtitle-2 text-white text-truncate font-weight-bold"
                >
                  Apply Changes
                </div>
              </v-btn>
            </v-col>
          </v-row>
          <v-alert
            v-else-if="applySuccess"
            type="success"
            density="compact"
            variant="tonal"
            class="text-caption"
            data-cy="config-success"
          >
            Settings sent to the thermostat.
          </v-alert>
        </div>
        <div class="text-center text-caption text-sonicSilver mt-2">
          Last updated{{ thermostatInfo.timeSinceLastUpdate }}
        </div>
      </v-card-text>
      <!--Footer actions-->
      <v-divider />
      <v-card-actions class="footer-actions justify-center pb-0">
        <ActionButtonsVue
          :buttonsList="actionButtons"
          :disabled="!userCanUpdate"
        />
      </v-card-actions>
      <!--Confirm Dialogs - Delete-->
      <ConfirmDialogDeleteThermostat
        v-if="showConfirmDialogDelete"
        :dialog="showConfirmDialogDelete"
        :thermostatInfo="thermostatInfo"
        @update:dialog="(value) => (showConfirmDialogDelete = value)"
      />
      <!--Confirm Dialogs - Schedule-->
      <ConfirmDialogScheduleThermostat
        v-if="showConfirmDialogSchedule"
        :dialog="showConfirmDialogSchedule"
        :thermostatInfo="thermostatInfo"
        @update:dialog="(value) => (showConfirmDialogSchedule = value)"
      />
      <!--Confirm Dialogs - Admin-->
      <ConfirmDialogAdminThermostat
        v-if="showConfirmDialogAdmin"
        :dialog="showConfirmDialogAdmin"
        :thermostatInfo="thermostatInfo"
        @update:dialog="(value) => (showConfirmDialogAdmin = value)"
      />
      <!--History Dialog-->
      <ThermostatSettingsHistoryDialog
        v-if="isSettingsHistory"
        :dialog="showSettingsHistoryDialog"
        @update:dialog="
          (value) => {
            isSettingsHistory = value;
            showSettingsHistoryDialog = value;
          }
        "
        :controlDetails="controlDetails"
        @update:settingsHistoryList="() => (isSettingsHistoryLoading = false)"
      />
    </v-card>
  </v-dialog>
</template>

<script>
import { RemapObjectKeysAdapter } from '@/Core.Patterns/Adapter';
import { CompositeObject } from '@/Core.Patterns/Composite';
import API from '@/Core.Service.Domain/Controls/Thermostats/API';
import {
  isLoraSchedulesEnabled,
  isLoraAdminDashboardEnabled,
  isTemperatureConversionTooltipEnabled,
  displaySiteName,
} from '@/configuration/featureFlags';
import {
  ThermostatDeviceSubType,
  MILESIGHT_TARGET_RANGE,
  isKeypadUnlocked,
  getThermostatActivity,
} from '@/Core.Service.Domain/Controls/Thermostats/types/Thermostat';

import CO2Widget from '@/Core.Service.Domain/Controls/Thermostats/Components/CO2Widget.vue';
import ConfirmDialogAdminThermostat from '@/Core.Service.Domain/Controls/Thermostats/Components/ConfirmDialogAdminThermostat.vue';
import ConfirmDialogScheduleThermostat from '@/Core.Service.Domain/Controls/Thermostats/Components/ConfirmDialogScheduleThermostat.vue';
import ConfirmDialogDeleteThermostat from '@/Core.Service.Domain/Controls/Thermostats/Components/ConfirmDialogDeleteThermostat.vue';
import ThermostatAlert from '@/Core.Service.Domain/Controls/Thermostats/Components/ThermostatAlert.vue';
import ThermostatSettingsHistoryDialog from '@/Core.Service.Domain/Controls/Thermostats/Components/ThermostatSettingsHistoryDialog.vue';
import ActionButtonsVue from '@/Core.UI.Domain/Components/ActionButtons';

import eventBus from '@/Core.Service.Domain/Shared/eventBus.js';

export default {
  name: 'ThermostatConfigDialog',

  components: {
    ActionButtonsVue,
    CO2Widget,
    ConfirmDialogAdminThermostat,
    ConfirmDialogScheduleThermostat,
    ConfirmDialogDeleteThermostat,
    ThermostatAlert,
    ThermostatSettingsHistoryDialog,
  },

  props: {
    dialog: {
      type: Boolean,
      required: true,
    },
    thermostatInfo: {
      type: Object,
      required: true,
    },
  },

  emits: ['update:dialog', 'set-thermostat-config'],

  created() {
    eventBus.$on('isConfigLoading', this.handleConfigLoading);
    eventBus.$on('isAdminLoading', this.handleAdminLoading);
    this.setTemperaturesOutOfRange();
  },

  beforeUnmount() {
    eventBus.$off('isConfigLoading', this.handleConfigLoading);
    eventBus.$off('isAdminLoading', this.handleAdminLoading);
  },

  data(instance) {
    // Only Milesight/LoRa thermostats have the in-app schedule pages, and only
    // once LORA_SCHEDULES is on. Everything else keeps opening the external
    // Pelican Schedule Dashboard.
    const isMilesightDevice =
      instance.thermostatInfo.deviceSubTypeId ===
      ThermostatDeviceSubType.MilesightLoRa;

    const usesLoraSchedules = isLoraSchedulesEnabled() && isMilesightDevice;
    const temperatureConversionTooltipEnabled =
      isTemperatureConversionTooltipEnabled();

    // The Schedule and Admin footer actions both open the external Pelican
    // management site, which a LoRa device has no presence on. Each is gated by
    // its own flag on Milesight tiles — LORA_SCHEDULES swaps Schedule over to
    // the in-app page, while LORA_ADMIN_DASHBOARD just reveals Admin as-is.
    // Pelican tiles are unaffected by either flag.
    const showsAdminAction =
      !isMilesightDevice || isLoraAdminDashboardEnabled();
    const showsScheduleAction = !isMilesightDevice || usesLoraSchedules;

    return {
      temperatureConversionTooltipEnabled,
      controlDetails: {
        name: instance.thermostatInfo.controlInfo.friendlyName,
        controlId: instance.thermostatInfo.controlId,
        siteLocationName: instance.thermostatInfo.controlInfo.siteLocationName,
      },
      isThermostatSetLoading: false,
      isSettingsHistoryLoading: false,
      showConfirmDialogDelete: false,
      showConfirmDialogSchedule: false,
      showConfirmDialogAdmin: false,
      isSettingsHistory: false,
      showSettingsHistoryDialog: false,
      applyErrorMessage: '',
      applySuccess: false,

      isDirty: {
        heatSetting: false,
        coolSetting: false,
        targetTemperature: false,
        mode: false,
        fanStatus: false,
        frontKeypad: false,
        isOutOfRange: false,
      },

      isDirtyNames: {
        heatSetting: 'Heat',
        coolSetting: 'Cool',
        targetTemperature: 'Target Temperature',
        mode: 'System Status',
        fanStatus: 'Fan Status',
        frontKeypad: 'Front Keypad',
      },

      staged: {
        heatSetting: instance.thermostatInfo.heatSetting,
        coolSetting: instance.thermostatInfo.coolSetting,
        targetTemperature: Math.round(
          instance.thermostatInfo.targetTemperature
        ),
        mode: instance.thermostatInfo.mode,
        fanStatus: instance.thermostatInfo.fanStatus,
        keypadLocked: !isKeypadUnlocked(instance.thermostatInfo.frontKeypad),
      },

      heatSettings: {
        maxHeat: instance.thermostatInfo.maxHeatSetting,
        minHeat: instance.thermostatInfo.minHeatSetting,
      },
      coolSettings: {
        maxCool: instance.thermostatInfo.maxCoolSetting,
        minCool: instance.thermostatInfo.minCoolSetting,
      },
      configLoadingList: [],
      adminLoadingList: [],

      actionButtons: [
        {
          icon: 'calendar-blank',
          toolTip: usesLoraSchedules
            ? 'Schedule Settings'
            : 'Schedule Dashboard',
          show: this.$can('update', 'controlschedules') && showsScheduleAction,
          action: () => {
            if (usesLoraSchedules) {
              instance.$router.push({
                name: 'ThermostatScheduleSettings',
                params: { thermostatId: instance.thermostatInfo.controlId },
              });
              return;
            }
            instance.showConfirmDialogSchedule = true;
            instance.configLoadingList.push(instance.thermostatInfo.controlId);
          },
          loading: () =>
            instance.configLoadingList.includes(
              instance.thermostatInfo.controlId
            ),
        },
        {
          icon: 'cog',
          toolTip: 'Admin Dashboard',
          show: this.$can('administer', 'control') && showsAdminAction,
          action: () => {
            instance.showConfirmDialogAdmin = true;
            instance.adminLoadingList.push(instance.thermostatInfo.controlId);
          },
          loading: () =>
            instance.adminLoadingList.includes(
              instance.thermostatInfo.controlId
            ),
        },
        {
          icon: 'pencil',
          toolTip: `Edit ${instance.$route.meta.toolTip}`,
          show: this.$can('update', 'controls'),
          action: () => {
            // this route will be replaced when the new edit page is ready
            instance.$router.push({
              name: 'ControlsEdit',
              params: {
                controlId: instance.thermostatInfo.controlId,
                type: 'thermostat',
              },
            });
          },
        },
        {
          icon: 'thermometer-off',
          toolTip: `Delete ${instance.$route.meta.toolTip}`,
          show: this.$can('delete', 'controls'),
          action: () => {
            instance.showConfirmDialogDelete = true;
          },
        },
        {
          icon: 'history',
          toolTip: `${instance.$route.meta.toolTip}'s Changes' History`,
          disabled: () =>
            instance.thermostatInfo.controlInfo.hasSettingsMessages === false,
          loading: () => instance.isSettingsHistoryLoading,
          action: () => {
            if (
              instance.thermostatInfo.controlInfo.hasSettingsMessages === false
            )
              return;
            instance.isSettingsHistoryLoading = true;
            instance.isSettingsHistory = true;
          },
        },
      ],
    };
  },

  computed: {
    // Redacted in the demo environment so recordings never show a real site.
    siteName() {
      return displaySiteName(this.thermostatInfo.controlInfo.siteLocationName);
    },

    isThermostatDirty() {
      return (
        Object.values(this.isDirty).some((value) => value) &&
        !this.isDirty.isOutOfRange
      );
    },

    userCanUpdate() {
      return this.$can('update', 'controls');
    },

    canEdit() {
      return this.userCanUpdate && !this.isDirty.isOutOfRange;
    },

    isMilesight() {
      return (
        this.thermostatInfo.deviceSubTypeId ===
        ThermostatDeviceSubType.MilesightLoRa
      );
    },

    targetSettings() {
      return MILESIGHT_TARGET_RANGE;
    },

    systemModes() {
      return this.isMilesight
        ? ['Auto', 'Cool', 'Heat', 'EM Heat', 'Off']
        : ['Heat', 'Cool', 'Auto', 'Off'];
    },

    // The WT201 fan vocabulary ('Auto'/'On'/'Circulate') is sent to the
    // device as-is by the LoRa settings route.
    fanModes() {
      return this.isMilesight ? ['Auto', 'On', 'Circulate'] : ['On', 'Auto'];
    },

    activity() {
      return getThermostatActivity(
        this.thermostatInfo.runStatus,
        this.thermostatInfo.mode
      );
    },

    activityColor() {
      if (this.activity === 'heating') return 'heatColor';
      if (this.activity === 'cooling') return 'intechBlue';
      return 'sonicSilver';
    },

    actionTarget() {
      if (this.isMilesight)
        return Math.round(this.thermostatInfo.targetTemperature);
      return this.activity === 'heating'
        ? this.thermostatInfo.heatSetting
        : this.thermostatInfo.coolSetting;
    },

    statusDotClass() {
      return this.isDirty.isOutOfRange
        ? 'status-dot--warning'
        : 'status-dot--ok';
    },

    pelicanSetpointColumns() {
      return [
        {
          key: 'heatSetting',
          icon: 'mdi-fire',
          color: 'heatColor',
          min: this.heatSettings.minHeat,
          max: this.heatSettings.maxHeat,
        },
        {
          key: 'coolSetting',
          icon: 'mdi-snowflake',
          color: 'intechBlue',
          min: this.coolSettings.minCool,
          max: this.coolSettings.maxCool,
        },
      ];
    },
  },

  watch: {
    'staged.heatSetting': function (newVal) {
      if (this.isDirty.isOutOfRange) return;
      this.isDirty.heatSetting = newVal !== this.thermostatInfo.heatSetting;
    },
    'staged.coolSetting': function (newVal) {
      if (this.isDirty.isOutOfRange) return;
      this.isDirty.coolSetting = newVal !== this.thermostatInfo.coolSetting;
    },
    'staged.targetTemperature': function (newVal) {
      if (this.isDirty.isOutOfRange) return;
      this.isDirty.targetTemperature =
        Number(newVal) !== Math.round(this.thermostatInfo.targetTemperature);
    },
    'staged.mode': function (newVal) {
      this.isDirty.mode = newVal !== this.thermostatInfo.mode;
    },
    'staged.fanStatus': function (newVal) {
      this.isDirty.fanStatus = newVal !== this.thermostatInfo.fanStatus;
    },
    'staged.keypadLocked': function (newVal) {
      this.isDirty.frontKeypad =
        newVal !== !isKeypadUnlocked(this.thermostatInfo.frontKeypad);
    },

    isSettingsHistoryLoading: {
      handler(newVal) {
        if (newVal === false) {
          this.showSettingsHistoryDialog = true;
        }
      },
    },
  },

  methods: {
    fahrenheitToCelsius(value) {
      return Math.round(((Number(value) - 32) * 5) / 9);
    },

    handleConfigLoading(value) {
      if (!value) {
        this.configLoadingList = [];
      }
    },

    handleAdminLoading(value) {
      if (!value) {
        this.adminLoadingList = [];
      }
    },

    stepSetpoint(key, direction) {
      const ranges = {
        heatSetting: [this.heatSettings.minHeat, this.heatSettings.maxHeat],
        coolSetting: [this.coolSettings.minCool, this.coolSettings.maxCool],
        targetTemperature: [this.targetSettings.min, this.targetSettings.max],
      };
      const [min, max] = ranges[key];
      const next = Number(this.staged[key]) + direction;
      this.staged[key] = Math.min(max, Math.max(min, next));
    },

    async handleApply() {
      if (!this.userCanUpdate) {
        return;
      }

      this.controlAnchorPosition();
      this.isThermostatSetLoading = true;
      this.applyErrorMessage = '';
      this.applySuccess = false;
      const sanitizedData = this.isMilesight
        ? this.sanitizeMilesightThermostatData()
        : this.sanitizeThermostatData();

      try {
        await Promise.all([
          API.setThermostatData(sanitizedData),
          this.cacheThermostats(sanitizedData),
        ]);
        this.$emit('set-thermostat-config');
        this.resetIsDirty();
        this.applySuccess = true;
      } catch (error) {
        this.applyErrorMessage =
          error?.error ||
          'Failed to send settings to the thermostat. Please try again.';
      } finally {
        this.isThermostatSetLoading = false;
      }
    },

    controlAnchorPosition() {
      localStorage.setItem(
        'controlAnchorPosition',
        `#thermostat_${this.thermostatInfo.controlId}`
      );
    },

    sanitizeThermostatData() {
      const thermostat = CompositeObject(
        {
          heatSetting: this.staged.heatSetting,
          coolSetting: this.staged.coolSetting,
          mode: this.staged.mode,
          fanStatus: this.staged.fanStatus,
          frontKeypad: this.staged.keypadLocked ? 'On' : 'Off',
        },
        this.thermostatInfo,
        [
          'minHeatSetting',
          'maxHeatSetting',
          'minCoolSetting',
          'maxCoolSetting',
          'controlId',
          'usageDateTime',
        ]
      );

      RemapObjectKeysAdapter(thermostat, ['controlId'], ['thermostatId']);

      return thermostat;
    },

    sanitizeMilesightThermostatData() {
      // The LoRa settings route matches changedProperties keys case-sensitively
      // against the API's PascalCase property names; values are the previous
      // settings, and Milesight keypads are written as 'Locked'/'Unlocked'.
      const changedProperties = {};

      if (this.isDirty.targetTemperature) {
        changedProperties.TargetTemperature =
          this.thermostatInfo.targetTemperature;
      }
      if (this.isDirty.mode) {
        changedProperties.Mode = this.thermostatInfo.mode;
      }
      if (this.isDirty.fanStatus) {
        changedProperties.FanStatus = this.thermostatInfo.fanStatus;
      }
      if (this.isDirty.frontKeypad) {
        changedProperties.FrontKeypad = isKeypadUnlocked(
          this.thermostatInfo.frontKeypad
        )
          ? 'Unlocked'
          : 'Locked';
      }

      return {
        thermostatId: this.thermostatInfo.controlId,
        usageDateTime: this.thermostatInfo.usageDateTime,
        targetTemperature: Math.round(Number(this.staged.targetTemperature)),
        mode: this.staged.mode,
        fanStatus: this.staged.fanStatus,
        frontKeypad: this.staged.keypadLocked ? 'Locked' : 'Unlocked',
        changedProperties,
      };
    },

    cacheThermostats(thermostat) {
      try {
        const thermostats = JSON.parse(
          localStorage.getItem('local-storage-thermostats') || '[]'
        );

        const foundIndex = thermostats.findIndex(
          (item) => item.thermostatId === thermostat.thermostatId
        );
        if (foundIndex > -1) {
          thermostats[foundIndex] = thermostat;
        } else {
          thermostats.push(thermostat);
        }

        localStorage.setItem(
          'local-storage-thermostats',
          JSON.stringify(thermostats)
        );
      } catch (error) {
        return Promise.reject(error);
      }
    },

    setTemperaturesOutOfRange() {
      if (this.getTemperaturesOutOfRange().length) {
        this.isDirty.isOutOfRange = true;
      }
    },

    getTemperaturesOutOfRange() {
      const {
        coolSetting,
        heatSetting,
        targetTemperature,
        minHeatSetting,
        maxHeatSetting,
        minCoolSetting,
        maxCoolSetting,
      } = this.thermostatInfo;

      const outOfRangeSettings = [];

      if (this.isMilesight) {
        const { min, max } = this.targetSettings;
        if (targetTemperature < min || targetTemperature > max) {
          outOfRangeSettings.push({
            name: 'Target Temperature',
            expected: `${min}-${max}`,
            current: targetTemperature,
          });
        }
        return outOfRangeSettings;
      }

      if (heatSetting < minHeatSetting || heatSetting > maxHeatSetting) {
        outOfRangeSettings.push({
          name: 'Heat',
          expected: `${minHeatSetting}-${maxHeatSetting}`,
          current: heatSetting,
        });
      }

      if (coolSetting < minCoolSetting || coolSetting > maxCoolSetting) {
        outOfRangeSettings.push({
          name: 'Cool',
          expected: `${minCoolSetting}-${maxCoolSetting}`,
          current: coolSetting,
        });
      }
      return outOfRangeSettings;
    },

    getThermostatNewSettings() {
      const dirtySettings = [];

      Object.entries(this.isDirty).forEach(([setting, isDirty]) => {
        if (isDirty) {
          if (setting === 'frontKeypad')
            return dirtySettings.push({
              name: this.isDirtyNames[setting],
              newSetting: this.staged.keypadLocked ? 'Locked' : 'Unlocked',
              currentSetting: isKeypadUnlocked(this.thermostatInfo.frontKeypad)
                ? 'Unlocked'
                : 'Locked',
            });
          const name = this.isDirtyNames[setting];
          const newSetting = this.staged[setting];
          const currentSetting = this.thermostatInfo[setting];
          dirtySettings.push({ name, newSetting, currentSetting });
        }
      });

      return dirtySettings;
    },

    handleCO2Action() {
      const controlFriendlyName = `${this.thermostatInfo.controlInfo.name}-${this.thermostatInfo.controlInfo.friendlyName}`;

      const { id } = this.thermostatInfo.controlInfo.site;
      this.$router.push({
        name: 'ControlsReportRoute',
        params: {
          reportName: 'CO2 Report',
          siteId: id,
          controlFriendlyName,
        },
      });
    },

    resetIsDirty() {
      this.isDirty = {
        heatSetting: false,
        coolSetting: false,
        targetTemperature: false,
        mode: false,
        fanStatus: false,
        frontKeypad: false,
        isOutOfRange: false,
      };
    },

    resetAllValues() {
      this.staged = {
        heatSetting: this.thermostatInfo.heatSetting,
        coolSetting: this.thermostatInfo.coolSetting,
        targetTemperature: Math.round(this.thermostatInfo.targetTemperature),
        mode: this.thermostatInfo.mode,
        fanStatus: this.thermostatInfo.fanStatus,
        keypadLocked: !isKeypadUnlocked(this.thermostatInfo.frontKeypad),
      };
      this.applyErrorMessage = '';
      this.applySuccess = false;
      this.resetIsDirty();
    },
  },
};
</script>

<style lang="scss" scoped>
.config-header {
  line-height: 1.2;
}

.status-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  vertical-align: middle;
}

.status-dot--ok {
  background-color: rgb(var(--v-theme-greenGraph));
}

.status-dot--warning {
  background-color: rgb(var(--v-theme-warning));
}

.setpoint-panel {
  background-color: rgba(var(--v-theme-primary), 0.04);
  height: 100%;
}

.setpoint-panel--off {
  opacity: 0.4;
  pointer-events: none;
}

// Subtle raised look for the setpoint steppers.
.step-btn {
  background: linear-gradient(
    180deg,
    rgb(var(--v-theme-surface)) 0%,
    rgba(var(--v-theme-primary), 0.08) 100%
  );
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.85),
    0 1px 2px rgba(0, 0, 0, 0.14),
    0 2px 5px rgba(0, 0, 0, 0.08);
}

.step-btn:active {
  transform: translateY(1px);
  box-shadow:
    inset 0 1px 2px rgba(0, 0, 0, 0.1),
    0 1px 1px rgba(0, 0, 0, 0.08);
}

.step-btn.v-btn--disabled {
  background: transparent;
  box-shadow: none;
}

// Fixed-height slot holding the Discard/Apply bar, the apply-success notice,
// or nothing — all three states share the same footprint, so swapping between
// them never shifts the layout.
.apply-slot {
  height: 44px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

// The card's pa-2 adds 8px below the actions (pb-0 rebalances it), and the
// shared ActionButtons margins are asymmetric (12px/5px) — even them out so
// the group is truly centered.
.footer-actions :deep(.action-button) {
  margin-left: 8px;
  margin-right: 8px;
}

.control-dirty-icon,
.control-co2-widget {
  cursor: pointer;
}

.v-overlay :deep(.control-tooltip) {
  background: none !important;
}

.v-overlay :deep(.v-alert__border) {
  opacity: 1 !important;
}
</style>
